import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/auth/store';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { computeDeltaState } from '@/features/counter-readings/format';
import { useRecordCounterReading } from '@/features/counter-readings/queries';
import type { CounterReading } from '@/features/counter-readings/types';
import type { CounterFieldConfig } from '@/features/equipment-types/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface RecordCounterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  equipmentId: string;
  field: CounterFieldConfig;
  lastReading: CounterReading | null;
  workOrderId?: string;
}

export function RecordCounterModal({
  open,
  onOpenChange,
  equipmentId,
  field,
  lastReading,
  workOrderId,
}: RecordCounterModalProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isSuperadmin = user?.roles.includes('superadmin') ?? false;
  const recordMut = useRecordCounterReading(equipmentId);

  const [valueStr, setValueStr] = useState('');
  const [readAtStr, setReadAtStr] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState('');
  const [forceAccept, setForceAccept] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Reset on close so re-open starts fresh.
  useEffect(() => {
    if (!open) {
      setValueStr('');
      setReadAtStr(new Date().toISOString().slice(0, 10));
      setNotes('');
      setForceAccept(false);
      setErrorKey(null);
      recordMut.reset();
    }
  }, [open, recordMut]);

  const value = Number.parseFloat(valueStr);
  const validValue = Number.isFinite(value);

  const delta = useMemo(() => {
    if (!validValue) return null;
    const readAtIso = new Date(readAtStr).toISOString();
    return computeDeltaState({ value, readAt: readAtIso }, lastReading, field);
  }, [readAtStr, lastReading, field, validValue, value]);

  const requiresNotes =
    forceAccept ||
    (delta !== null && delta.kind === 'warning');
  const submitDisabled =
    !validValue ||
    recordMut.isPending ||
    (delta?.kind === 'error' && !forceAccept) ||
    (requiresNotes && notes.trim().length === 0);

  const handleSubmit = () => {
    setErrorKey(null);
    if (!validValue) return;
    recordMut.mutate(
      {
        equipmentId,
        field: field.field,
        value,
        unit: field.unit,
        readAt: new Date(readAtStr).toISOString(),
        workOrderId,
        notes: notes.trim() || undefined,
        forceAccept: forceAccept || undefined,
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => {
          setErrorKey(
            err instanceof ApiError ? err.key : 'counter.record.saveError',
          );
        },
      },
    );
  };

  const fieldLabel =
    t(`counter.field.${field.field}`, { defaultValue: field.label });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('counter.record.title', { label: fieldLabel })}</DialogTitle>
          <DialogDescription>
            {t('counter.record.description')}
          </DialogDescription>
        </DialogHeader>

        {errorKey && (
          <Alert variant="destructive">
            <AlertTitle>{t('counter.record.saveError')}</AlertTitle>
            <AlertDescription>
              {t(errorKey, { defaultValue: t('counter.record.saveError') })}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="counter-value" className="text-sm font-medium">
              {t('counter.record.valueLabel')} ({field.unit})
            </label>
            <Input
              id="counter-value"
              type="number"
              step="0.01"
              min="0"
              value={valueStr}
              onChange={(e) => setValueStr(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="counter-read-at" className="text-sm font-medium">
              {t('counter.record.readAtLabel')}
            </label>
            <Input
              id="counter-read-at"
              type="date"
              value={readAtStr}
              onChange={(e) => setReadAtStr(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="counter-notes" className="text-sm font-medium">
              {t('counter.record.notesLabel')}
              {requiresNotes && <span className="ml-1 text-red-700">*</span>}
            </label>
            <Input
              id="counter-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {delta && (
            <DeltaPreview
              delta={delta}
              field={field}
              lastReading={lastReading}
            />
          )}

          {isSuperadmin && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={forceAccept}
                  onCheckedChange={setForceAccept}
                  aria-label={t('counter.record.forceAcceptLabel')}
                />
                <span className="text-sm font-medium text-amber-900">
                  {t('counter.record.forceAcceptLabel')}
                </span>
              </div>
              <p className="text-xs text-amber-800">
                {t('counter.record.forceAcceptHint')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={recordMut.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {recordMut.isPending && (
              <Loader2 className="animate-spin" aria-hidden />
            )}
            {t('counter.record.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeltaPreview({
  delta,
  field,
  lastReading,
}: {
  delta: NonNullable<ReturnType<typeof computeDeltaState>>;
  field: CounterFieldConfig;
  lastReading: CounterReading | null;
}) {
  const { t } = useTranslation();
  if (delta.kind === 'first') {
    return (
      <p className="text-xs text-muted-foreground">
        {t('counter.record.deltaFirst')}
      </p>
    );
  }
  const deltaStr = delta.delta.toFixed(2);
  const maxStr = delta.maxDelta.toFixed(0);
  const days = delta.daysSinceLast.toFixed(1);
  const lastValue = lastReading ? Number(lastReading.value).toFixed(2) : '0';
  const lastDate = lastReading
    ? new Date(lastReading.readAt).toLocaleDateString()
    : '';
  const percent = ((delta.delta / delta.maxDelta) * 100).toFixed(0);
  const tone =
    delta.kind === 'error'
      ? 'bg-red-100 text-red-800 border-red-200'
      : delta.kind === 'warning'
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-blue-100 text-blue-800 border-blue-200';
  let body = t('counter.record.deltaInfo', {
    delta: deltaStr,
    unit: field.unit,
    date: lastDate,
  });
  if (delta.reason === 'decreasing') {
    body = t('counter.record.errorDecreasing', {
      lastValue,
      unit: field.unit,
    });
  } else if (delta.reason === 'rateExceeded') {
    body = t('counter.record.errorRateExceeded', {
      delta: deltaStr,
      days,
      maxDelta: maxStr,
      unit: field.unit,
    });
  } else if (delta.reason === 'rateNearLimit') {
    body = t('counter.record.warning80pct', {
      percent,
      maxDelta: maxStr,
      unit: field.unit,
    });
  }
  return (
    <p
      className={cn(
        'rounded-md border px-3 py-2 text-xs',
        tone,
      )}
      role="status"
    >
      {body}
    </p>
  );
}
